// src/components/onboarding/stage6-human-review.tsx
//
// Stage 6 — Human review & Cohen's weighted Kappa (Sprint 3, in-product).
//
// The reviewer reads each raw AI response with the judge's score + evidence
// visible, then enters independent judgments. The stratified sample, human
// scores, and linear-weighted Kappa are stored as structured DB records and
// the IRR report is downloadable as HTML or CSV.
//
import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, Check, X, Scale, RefreshCw, Sparkles,
  ChevronLeft, ChevronRight, Download, Info, Upload, HelpCircle,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import type { CustomerDetail } from "@/api/onboarding-api";
import { runStage6 } from "@/api/onboarding-api";
import {
  getScoringState,
  generateReviewSample,
  getReviewSession,
  submitReviewItem,
  computeSessionKappa,
  downloadKappaExport,
  downloadReviewItemsCsv,
  importReviewItemsCsv,
  type ImportReviewItemsResult,
  type ReviewSessionDetail,
  type ReviewItem,
  type KappaResult,
} from "@/api/onboarding-scoring-api";

interface Props {
  customer: CustomerDetail;
  onCustomerUpdate?: (c: CustomerDetail) => void;
  onStageComplete?: () => void;
}

export function Stage6HumanReview({
  customer,
  onCustomerUpdate,
  onStageComplete,
}: Props) {
  const runId = customer.measurement_run_id ?? null;

  const [loading, setLoading] = useState(true);
  const [seed, setSeed] = useState(42);
  const [creating, setCreating] = useState(false);
  const [session, setSession] = useState<ReviewSessionDetail | null>(null);
  const [activeSessions, setActiveSessions] = useState<
    { id: number; reviewer: string; status: string; overall_kappa: number | null }[]
  >([]);
  const [idx, setIdx] = useState(0);
  const [kappa, setKappa] = useState<KappaResult | null>(null);
  const [savingItem, setSavingItem] = useState(false);
  const [computing, setComputing] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const [showKappaHelp, setShowKappaHelp] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [importReport, setImportReport] = useState<ImportReviewItemsResult | null>(null);
  const [finalising, setFinalising] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const csvInputRef = React.useRef<HTMLInputElement | null>(null);

  /**
   * Advance to Stage 7 with NO human-review session.
   *
   * Human review is optional to the system and was mandatory only in this
   * screen: the Continue button needed every item marked and a Kappa computed,
   * and there was no other way out — while POST /stages/6/run has always
   * advanced the stage with no session at all. Customer 14 was moved to Stage 7
   * through the API on 2026-09-12 for exactly that reason.
   *
   * Kappa is recorded as NOT COMPUTED, never as a number and never as a bare
   * null, and the review screen stays available afterwards for anyone who wants
   * the agreement statistic.
   */
  const continueWithoutReview = async () => {
    setSkipping(true);
    try {
      const res = await runStage6(customer.id, true);
      if (res.customer) onCustomerUpdate?.(res.customer);
      toast.success("Stage 6 complete — Kappa recorded as not computed.");
      onStageComplete?.();
    } catch (e: any) {
      toast.error(e?.message || "Could not complete Stage 6");
    } finally {
      setSkipping(false);
    }
  };

  const skipReviewButton = (
    <button
      onClick={continueWithoutReview}
      disabled={skipping}
      title="Advance to Stage 7 without an inter-rater review. Kappa is recorded as not computed."
      className="flex items-center gap-1.5 px-4 py-2 rounded-lg"
      style={{
        backgroundColor: "transparent",
        color: skipping ? "var(--muted-foreground)" : "var(--foreground)",
        fontSize: "var(--text-xs)",
        fontWeight: 600,
        border: "1px solid var(--border)",
        cursor: skipping ? "not-allowed" : "pointer",
      }}
    >
      {skipping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
      Continue without review
      {!skipping && <ArrowRight className="w-3.5 h-3.5" />}
    </button>
  );

  useEffect(() => {
    if (runId == null) {
      setLoading(false);
      return;
    }
    getScoringState(customer.id, runId)
      .then((s) => {
        setActiveSessions(
          s.review_sessions.map((r) => ({
            id: r.id,
            reviewer: r.reviewer,
            status: r.status,
            overall_kappa: r.overall_kappa,
          })),
        );
        const open = s.review_sessions[0];
        if (open) return getReviewSession(open.id).then(setSession);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id, runId]);

  const items = session?.items ?? [];
  const current: ReviewItem | undefined = items[idx];
  const reviewedCount = useMemo(
    () => items.filter((i) => i.reviewed).length,
    [items],
  );

  if (runId == null) {
    return (
      <Card title="Human review — measurement run required">
        <p style={pMuted}>
          Stage 6 validates the judge against an independent human read.
          Complete Stage 4 (measurement) and Stage 5 (scoring) first.
        </p>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card title="Human review & Kappa">
        <RunningRow label="Loading review state…" />
      </Card>
    );
  }

  const makeSample = async () => {
    setCreating(true);
    try {
      const detail = await generateReviewSample(customer.id, runId, seed);
      setSession(detail);
      setIdx(0);
      setKappa(null);
      toast.success(
        `Sample created: ${detail.session.mention_positive_count} mention-positive + ${detail.session.control_count} control`,
      );
    } catch (e: any) {
      toast.error(e?.message || "Could not create sample");
    } finally {
      setCreating(false);
    }
  };

  const reload = async () => {
    if (!session) return;
    const d = await getReviewSession(session.session.id);
    setSession(d);
  };

  const exportKappa = async (fmt: "html" | "csv") => {
    if (!session) return;
    setExporting(fmt);
    try {
      await downloadKappaExport(session.session.id, fmt);
    } catch (e: any) {
      toast.error(e?.message || "Export failed");
    } finally {
      setExporting(null);
    }
  };

  const handleDownloadReviewCsv = async () => {
    if (!session) return;
    setDownloadingCsv(true);
    try {
      await downloadReviewItemsCsv(session.session.id);
      toast.success("Review items downloaded. Edit the human_* columns and re-upload.");
    } catch (e: any) {
      toast.error(e?.message || "Download failed");
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleCsvFilePicked = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !session) return;
    setUploadingCsv(true);
    setImportReport(null);
    try {
      const report = await importReviewItemsCsv(session.session.id, file, false);
      setImportReport(report);
      if (report.committed) {
        toast.success(
          `Imported ${report.updated} row${report.updated === 1 ? "" : "s"}` +
            (report.skipped ? ` · ${report.skipped} blank row${report.skipped === 1 ? "" : "s"} skipped` : ""),
        );
        await reload();
      } else {
        toast.error(
          `Import rejected: ${report.errors.length} validation error${report.errors.length === 1 ? "" : "s"}. No rows committed.`,
        );
      }
    } catch (e: any) {
      toast.error(e?.message || "Upload failed");
    } finally {
      setUploadingCsv(false);
    }
  };

  if (!session) {
    return (
      <Card
        title="Generate human-review sample"
        subtitle="Stratified: up to 80 mention-positive + 20 control rows, fixed seed = reproducible."
      >
        <p style={pMuted}>
          Stage 5 scores must exist for this run. The reviewer then reads each
          raw response with the judge's score and evidence visible and enters
          an independent judgment. Cohen's weighted Kappa (linear) quantifies
          agreement per reasoning variable.
        </p>
        <div className="flex items-end gap-3 mt-4">
          <div>
            <div className="text-xs uppercase mb-1.5" style={labelStyle}>Random seed</div>
            <input
              type="number"
              value={seed}
              onChange={(e) => setSeed(Number(e.target.value) || 0)}
              className="w-32"
              style={inputStyle}
            />
          </div>
          <button
            onClick={makeSample}
            disabled={creating}
            className="px-5 py-2.5 rounded-lg flex items-center gap-2"
            style={{
              backgroundColor: "var(--alpha-teal)", color: "white",
              fontSize: "var(--text-sm)", fontWeight: 600, border: "none",
              cursor: creating ? "not-allowed" : "pointer",
            }}
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />}
            Generate sample
          </button>
          {onStageComplete && skipReviewButton}
        </div>
        <p style={{ fontSize: 11, color: "var(--muted-foreground)", margin: "10px 0 0 0", lineHeight: 1.5 }}>
          Human review is optional. Continuing without it advances to Stage 7 and
          records inter-rater agreement as <strong>not computed</strong> — there
          will be no Kappa to quote for this run. You can come back and generate
          a sample at any time.
        </p>
        {activeSessions.length > 0 && (
          <div className="mt-5">
            <div className="text-xs uppercase mb-2" style={labelStyle}>Previous sessions</div>
            <div className="space-y-2">
              {activeSessions.map((s) => (
                <button
                  key={s.id}
                  onClick={() =>
                    getReviewSession(s.id).then(setSession).catch(() => {})
                  }
                  className="w-full flex items-center justify-between p-3 rounded-lg text-left"
                  style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)", cursor: "pointer" }}
                >
                  <span style={{ fontSize: "var(--text-sm)", color: "var(--foreground)" }}>
                    Session #{s.id} · {s.reviewer}
                  </span>
                  <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                    {s.status}
                    {s.overall_kappa != null && ` · κ ${s.overall_kappa.toFixed(2)}`}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>
    );
  }

  const rvs = session.reasoning_variables;
  const hasKappa = kappa != null || session.session.overall_kappa != null;

  return (
    <div className="space-y-5">
      {/* Header / progress */}
      <Card title={`Human review — session #${session.session.id}`}
        subtitle={`Judge: ${session.session.scorer_model_version || "—"} · rubric ${session.session.rubric_version || "—"} · seed ${session.session.sample_seed}`}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <Stat label="Sample size" value={String(session.session.sample_size)} />
          <Stat label="Mention-positive" value={String(session.session.mention_positive_count)} />
          <Stat label="Control" value={String(session.session.control_count)} />
          <Stat
            label="Reviewed"
            value={`${reviewedCount}/${items.length}`}
            warn={reviewedCount < items.length}
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={idx === 0}
              className="p-2 rounded-md"
              style={navBtn(idx === 0)}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              Item {idx + 1} / {items.length}
            </span>
            <button
              onClick={() => setIdx((i) => Math.min(items.length - 1, i + 1))}
              disabled={idx >= items.length - 1}
              className="p-2 rounded-md"
              style={navBtn(idx >= items.length - 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={reload}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
              style={{ background: "transparent", color: "var(--muted-foreground)", fontSize: "var(--text-xs)", border: "1px solid var(--border)", cursor: "pointer" }}>
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={() => setShowKappaHelp((v) => !v)}
              title="Learn what Kappa is and what 'Compute Kappa' does"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
              style={{
                background: "transparent",
                color: showKappaHelp ? "var(--alpha-teal)" : "var(--muted-foreground)",
                fontSize: "var(--text-xs)", fontWeight: 600,
                border: `1px solid ${showKappaHelp ? "var(--alpha-teal)" : "var(--border)"}`,
                cursor: "pointer",
              }}
            >
              <HelpCircle className="w-3.5 h-3.5" /> What is Kappa?
            </button>
            <button
              onClick={async () => {
                setComputing(true);
                try {
                  const k = await computeSessionKappa(session.session.id, false);
                  setKappa(k);
                  await reload();
                  toast.success("Kappa computed.");
                } catch (e: any) {
                  toast.error(e?.message || "Kappa failed");
                } finally {
                  setComputing(false);
                }
              }}
              disabled={computing || reviewedCount === 0}
              title={
                reviewedCount === 0
                  ? "Review at least one item before computing Kappa"
                  : "Compute Cohen's weighted Kappa: aligns judge vs human scores for every reviewed item, returns a number from −1 to +1 quantifying agreement beyond chance, and stores the result for export."
              }
              className="flex items-center gap-2 px-4 py-2 rounded-lg"
              style={{
                backgroundColor: reviewedCount === 0 ? "var(--muted)" : "var(--alpha-teal)",
                color: reviewedCount === 0 ? "var(--muted-foreground)" : "white",
                fontSize: "var(--text-sm)", fontWeight: 600, border: "none",
                cursor: computing || reviewedCount === 0 ? "not-allowed" : "pointer",
              }}
            >
              {computing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />}
              Compute Kappa
            </button>
          </div>
        </div>
      </Card>

      {showKappaHelp && <KappaHelpPanel onClose={() => setShowKappaHelp(false)} />}

      {/* Bulk CSV round-trip — edit offline in Excel / Sheets */}
      <Card
        title="Bulk review — edit offline"
        subtitle="Download all items, fill the human_* columns in Excel or Sheets, then upload to update every row in one shot."
      >
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleDownloadReviewCsv}
            disabled={downloadingCsv}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
            style={{
              background: "transparent",
              color: downloadingCsv ? "var(--muted-foreground)" : "var(--signal-blue)",
              fontSize: "var(--text-xs)", fontWeight: 600,
              border: `1px solid ${downloadingCsv ? "var(--border)" : "var(--signal-blue)"}`,
              cursor: downloadingCsv ? "not-allowed" : "pointer",
            }}
          >
            {downloadingCsv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Download CSV
          </button>
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleCsvFilePicked}
            style={{ display: "none" }}
          />
          <button
            onClick={() => csvInputRef.current?.click()}
            disabled={uploadingCsv}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
            style={{
              background: "transparent",
              color: uploadingCsv ? "var(--muted-foreground)" : "var(--alpha-teal)",
              fontSize: "var(--text-xs)", fontWeight: 600,
              border: `1px solid ${uploadingCsv ? "var(--border)" : "var(--alpha-teal)"}`,
              cursor: uploadingCsv ? "not-allowed" : "pointer",
            }}
          >
            {uploadingCsv ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Upload edited CSV
          </button>
          <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            Keep the <code>item_id</code> column. Scores must be 0, 1, 2, or blank. <code>human_mentioned</code> takes yes / no / blank.
          </span>
        </div>

        {importReport && (
          <div
            className="mt-3 p-3 rounded-md"
            style={{
              backgroundColor: "var(--muted)",
              border: `1px solid ${importReport.committed ? "var(--alpha-teal)" : "var(--infrared-coral)"}`,
            }}
          >
            <div style={{ fontSize: "var(--text-xs)", fontWeight: 700, color: "var(--foreground)", marginBottom: 4 }}>
              {importReport.committed
                ? `Imported ${importReport.updated} row${importReport.updated === 1 ? "" : "s"}`
                : `Import rejected — ${importReport.errors.length} validation error${importReport.errors.length === 1 ? "" : "s"}`}
            </div>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              Total rows: {importReport.total_rows} · Skipped (blank): {importReport.skipped} · Updated: {importReport.updated}
            </div>
            {importReport.errors.length > 0 && (
              <ul style={{ margin: "8px 0 0 18px", padding: 0, fontSize: "var(--text-xs)", color: "var(--infrared-coral)", maxHeight: 160, overflowY: "auto" }}>
                {importReport.errors.slice(0, 50).map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
                {importReport.errors.length > 50 && (
                  <li style={{ color: "var(--muted-foreground)" }}>
                    …and {importReport.errors.length - 50} more
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </Card>

      {/* Kappa results + exports */}
      {hasKappa && (
        <Card title="Cohen's weighted Kappa (linear)">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
            <KappaStat
              label="Overall (variables)"
              k={kappa?.overall_kappa ?? session.session.overall_kappa}
              strength={kappa?.overall_strength}
            />
            <KappaStat
              label="Mention agreement"
              k={kappa?.mention_kappa ?? session.session.mention_kappa}
              strength={kappa?.mention_strength}
            />
            <Stat
              label="Coverage"
              value={
                kappa
                  ? `${kappa.reviewed_count}/${kappa.total_count}`
                  : `${reviewedCount}/${items.length}`
              }
            />
          </div>
          <div className="text-xs uppercase mb-2" style={labelStyle}>
            Per-variable agreement
          </div>
          <div className="space-y-3">
            {Object.entries(
              kappa?.per_variable_kappa ??
                session.session.per_variable_kappa ??
                {},
            ).map(([rv, info]) => {
              const k = info.kappa;
              const undefinedKappa = k == null;
              const good = !undefinedKappa && k >= 0.8;
              const mid = !undefinedKappa && k >= 0.6 && k < 0.8;
              const color = undefinedKappa
                ? "var(--muted-foreground)"
                : good
                ? "var(--alpha-teal)"
                : mid
                ? "var(--chart-3)"
                : "var(--infrared-coral)";
              const fillWidth = undefinedKappa
                ? 0
                : Math.max(0, Math.min(100, ((k as number) + 1) / 2 * 100));
              return (
                <div key={rv}>
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <span
                      style={{
                        fontSize: "var(--text-xs)",
                        color: "var(--foreground)",
                        fontWeight: 500,
                        wordBreak: "break-word",
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      {rv}
                    </span>
                    <span
                      style={{ fontSize: "var(--text-xs)", fontWeight: 600, color, whiteSpace: "nowrap" }}
                      title={
                        undefinedKappa
                          ? `Undefined: only ${info.n ?? 0} compared pair${(info.n ?? 0) === 1 ? "" : "s"} so far, or no variance in ratings`
                          : `${info.n ?? 0} compared pairs · agreement ${((info.agreement ?? 0) * 100).toFixed(0)}%`
                      }
                    >
                      {undefinedKappa ? "n/a (n=" + (info.n ?? 0) + ")" : `${k.toFixed(2)} · ${info.strength}`}
                    </span>
                  </div>
                  <div className="w-full rounded-full overflow-hidden" style={{ height: 6, backgroundColor: "var(--muted)" }}>
                    <div className="h-full" style={{ width: `${fillWidth}%`, backgroundColor: color }} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between pt-4 mt-4" style={{ borderTop: "1px solid var(--border)" }}>
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              IRR is stored in the database — export the report for the client file.
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => exportKappa("html")}
                disabled={exporting != null}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
                style={{
                  background: "transparent",
                  color: exporting ? "var(--muted-foreground)" : "var(--signal-blue)",
                  fontSize: "var(--text-xs)", fontWeight: 600,
                  border: `1px solid ${exporting ? "var(--border)" : "var(--signal-blue)"}`,
                  cursor: exporting ? "not-allowed" : "pointer",
                }}
              >
                {exporting === "html" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                HTML
              </button>
              <button
                onClick={() => exportKappa("csv")}
                disabled={exporting != null}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg"
                style={{
                  background: "transparent",
                  color: exporting ? "var(--muted-foreground)" : "var(--signal-blue)",
                  fontSize: "var(--text-xs)", fontWeight: 600,
                  border: `1px solid ${exporting ? "var(--border)" : "var(--signal-blue)"}`,
                  cursor: exporting ? "not-allowed" : "pointer",
                }}
              >
                {exporting === "csv" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                CSV
              </button>
              {/* A started-but-unfinished review is not a dead end: the system
                  has always been able to advance without one. Offered only
                  while Continue is blocked, so the reviewed path stays the
                  obvious one. */}
              {onStageComplete &&
                !(items.length > 0 && reviewedCount === items.length && hasKappa) &&
                skipReviewButton}
              {onStageComplete && (() => {
                const allReviewed = items.length > 0 && reviewedCount === items.length;
                const canContinue = allReviewed && hasKappa && !finalising;
                return (
                  <button
                    onClick={async () => {
                      if (!session) return;
                      setFinalising(true);
                      try {
                        // Re-fires the Kappa computation; the backend uses this
                        // call site to advance customers.current_stage 6 → 7
                        // once every item has been reviewed. Idempotent — the
                        // stored Kappa values do not change.
                        const k = await computeSessionKappa(session.session.id, false);
                        setKappa(k);
                        onStageComplete();
                      } catch (e: any) {
                        toast.error(e?.message || "Could not finalise Stage 6");
                      } finally {
                        setFinalising(false);
                      }
                    }}
                    disabled={!canContinue}
                    title={
                      !allReviewed
                        ? `Review every item before continuing (${reviewedCount}/${items.length} done).`
                        : !hasKappa
                        ? "Compute Kappa before continuing."
                        : "Mark Stage 6 complete and move to Stage 7."
                    }
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg"
                    style={{
                      backgroundColor: canContinue ? "var(--alpha-teal)" : "var(--muted)",
                      color: canContinue ? "white" : "var(--muted-foreground)",
                      fontSize: "var(--text-xs)", fontWeight: 600,
                      border: "none",
                      cursor: canContinue ? "pointer" : "not-allowed",
                    }}
                  >
                    {finalising ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Continue to Stage 7
                    {!finalising && <ArrowRight className="w-3.5 h-3.5" />}
                  </button>
                );
              })()}
            </div>
          </div>
        </Card>
      )}

      {/* Review item */}
      {current && (
        <ReviewItemCard
          key={current.id}
          item={current}
          rvs={rvs}
          saving={savingItem}
          onSubmit={async (payload) => {
            setSavingItem(true);
            try {
              await submitReviewItem(current.id, payload);
              setSession((prev) =>
                prev
                  ? {
                      ...prev,
                      items: prev.items.map((it) =>
                        it.id === current.id
                          ? {
                              ...it,
                              reviewed: true,
                              human_mentioned: payload.human_mentioned,
                              human_rv_scores: payload.human_rv_scores,
                              reviewer_notes: payload.reviewer_notes ?? "",
                            }
                          : it,
                      ),
                    }
                  : prev,
              );
              toast.success(`Item ${idx + 1} saved`);
              if (idx < items.length - 1) setIdx(idx + 1);
            } catch (e: any) {
              toast.error(e?.message || "Save failed");
            } finally {
              setSavingItem(false);
            }
          }}
        />
      )}
    </div>
  );
}

// ─── Review item card ────────────────────────────────────────────────────────

function ReviewItemCard({
  item,
  rvs,
  saving,
  onSubmit,
}: {
  item: ReviewItem;
  rvs: { id: string; claim: string }[];
  saving: boolean;
  onSubmit: (p: {
    human_mentioned: boolean | null;
    human_rv_scores: Record<string, number>;
    reviewer_notes: string;
  }) => void;
}) {
  const [mentioned, setMentioned] = useState<boolean | null>(
    item.human_mentioned ?? null,
  );
  const [scores, setScores] = useState<Record<string, number>>(
    () => ({ ...item.human_rv_scores }),
  );
  const [notes, setNotes] = useState(item.reviewer_notes || "");

  return (
    <Card title={`Response — ${item.model}`}
      subtitle={`Stratum: ${item.stratum.replace("_", " ")}${item.reviewed ? " · already reviewed" : ""}`}>
      {item.query_text && (
        <div className="mb-3">
          <div className="text-xs uppercase mb-1" style={labelStyle}>Query</div>
          <p style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", margin: 0 }}>{item.query_text}</p>
        </div>
      )}

      <div className="text-xs uppercase mb-1" style={labelStyle}>Raw AI response</div>
      <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)", maxHeight: 240, overflowY: "auto" }}>
        <p style={{ fontSize: "var(--text-xs)", color: "var(--foreground)", lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>
          {item.response_text || "(empty response)"}
        </p>
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg mb-4"
        style={{ backgroundColor: "var(--background)", border: "1px solid var(--border)" }}>
        <div>
          <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
            Brand mentioned?
          </div>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            Judge said: <strong>{item.scorer_mentioned ? "yes" : "no"}</strong>
          </div>
        </div>
        <div className="flex gap-2">
          <ToggleBtn active={mentioned === true} color="var(--alpha-teal)" onClick={() => setMentioned(true)}>
            <Check className="w-3.5 h-3.5" /> Yes
          </ToggleBtn>
          <ToggleBtn active={mentioned === false} color="var(--infrared-coral)" onClick={() => setMentioned(false)}>
            <X className="w-3.5 h-3.5" /> No
          </ToggleBtn>
        </div>
      </div>

      <div className="space-y-3">
        {rvs.map((rv) => {
          const opusScore = item.scorer_rv_scores?.[rv.id];
          const ev = item.scorer_rv_evidence?.[rv.id];
          const hv = scores[rv.id];
          return (
            <div key={rv.id} className="p-3 rounded-lg" style={{ backgroundColor: "var(--background)", border: "1px solid var(--border)" }}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <span style={{ fontSize: "var(--text-xs)", fontWeight: 700, color: "var(--alpha-teal)" }}>{rv.id}</span>
                  <p style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", margin: "2px 0 0 0" }}>{rv.claim}</p>
                </div>
                <span style={{ fontSize: 11, color: "var(--muted-foreground)", whiteSpace: "nowrap" }}>
                  judge: <strong style={{ color: "var(--foreground)" }}>{opusScore ?? "—"}</strong>
                </span>
              </div>
              {ev && (
                <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", fontStyle: "italic", margin: "0 0 8px 0", borderLeft: "2px solid var(--border)", paddingLeft: 8 }}>
                  “{ev}”
                </p>
              )}
              <div className="flex items-center gap-2">
                <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>Your score:</span>
                {[0, 1, 2].map((n) => {
                  const on = hv === n;
                  const matches = opusScore === n;
                  return (
                    <button
                      key={n}
                      onClick={() => setScores((p) => ({ ...p, [rv.id]: n }))}
                      className="w-8 h-8 rounded-md flex items-center justify-center"
                      style={{
                        backgroundColor: on ? "var(--alpha-teal)" : "var(--muted)",
                        color: on ? "white" : "var(--foreground)",
                        border: `1px solid ${on ? "var(--alpha-teal)" : matches ? "var(--alpha-teal-25)" : "var(--border)"}`,
                        fontSize: "var(--text-sm)", fontWeight: 700, cursor: "pointer",
                      }}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <div className="text-xs uppercase mb-1.5" style={labelStyle}>
          Reviewer notes (edge cases / judgment calls)
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full"
          style={{ ...inputStyle, resize: "vertical" }}
          placeholder="Optional — captures rationale that informs future rubric updates."
        />
      </div>

      <div className="flex justify-end pt-4 mt-4" style={{ borderTop: "1px solid var(--border)" }}>
        <button
          onClick={() =>
            onSubmit({
              human_mentioned: mentioned,
              human_rv_scores: scores,
              reviewer_notes: notes,
            })
          }
          disabled={saving}
          className="px-5 py-2.5 rounded-lg flex items-center gap-2"
          style={{
            backgroundColor: "var(--alpha-teal)", color: "white",
            fontSize: "var(--text-sm)", fontWeight: 600, border: "none",
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Save &amp; next
        </button>
      </div>
    </Card>
  );
}

// ─── Kappa explainer ─────────────────────────────────────────────────────────

function KappaHelpPanel({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="rounded-xl p-4 md:p-5"
      style={{
        backgroundColor: "var(--card)",
        border: "1px solid var(--alpha-teal-25, var(--border))",
        borderLeft: "3px solid var(--alpha-teal)",
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4" style={{ color: "var(--alpha-teal)" }} />
          <h3 style={{ fontSize: "var(--text-base)", fontWeight: 600, color: "var(--foreground)", margin: 0 }}>
            What Cohen&apos;s weighted Kappa means here
          </h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-md"
          style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--muted-foreground)" }}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", lineHeight: 1.6 }}>
        <p style={{ margin: "0 0 10px 0" }}>
          <strong>Kappa</strong> measures how well the AI judge and the human reviewer agree —
          beyond what you&apos;d expect by chance alone. It&apos;s how we know whether to trust
          the auto-scorer&apos;s 730-row output for the rest of the pipeline without checking
          every row by hand.
        </p>
        <p style={{ margin: "0 0 10px 0" }}>
          The scale runs from <strong>−1 to +1</strong>. Two numbers appear above:
        </p>
        <ul style={{ margin: "0 0 10px 18px", padding: 0 }}>
          <li><strong>Overall (variables)</strong> — linear-weighted Kappa across all 0/1/2 reasoning-variable scores. Linear weighting penalises a 0-vs-2 disagreement more than a 0-vs-1.</li>
          <li><strong>Mention agreement</strong> — unweighted Kappa on the binary &ldquo;brand mentioned: yes/no&rdquo; judgment.</li>
        </ul>
        <p style={{ margin: "0 0 10px 0" }}>
          Landis &amp; Koch strength bands: <em>slight</em> (&lt; 0.20), <em>fair</em> (&lt; 0.40),
          <em> moderate</em> (&lt; 0.60), <em>substantial</em> (&lt; 0.80), <em>almost perfect</em> (≥ 0.80).
          Substantial or better is the bar to clear before signing off on the judge.
        </p>

        <div
          className="rounded-md p-3 my-3"
          style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
        >
          <div style={{ fontSize: "var(--text-xs)", fontWeight: 700, color: "var(--foreground)", marginBottom: 4 }}>
            What &ldquo;Compute Kappa&rdquo; does
          </div>
          <ol style={{ margin: "0 0 0 18px", padding: 0, fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
            <li>Pulls every reviewed item in this session.</li>
            <li>Aligns the judge&apos;s score against your score per reasoning variable.</li>
            <li>Computes linear-weighted Kappa per variable, plus overall and mention Kappa.</li>
            <li>Stores results in the database and unlocks the HTML/CSV export below.</li>
          </ol>
        </div>

        <div
          className="rounded-md p-3"
          style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
        >
          <div style={{ fontSize: "var(--text-xs)", fontWeight: 700, color: "var(--foreground)", marginBottom: 4 }}>
            Why you may see <code>n/a</code>
          </div>
          <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: 0 }}>
            Kappa is mathematically undefined when there is no variance to weight: e.g. only a
            couple of items reviewed, every rating identical, or a variable where you didn&apos;t
            enter a 0/1/2 score. Aim for at least ~20 reviewed items with a mix of scores before
            treating the number as meaningful.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Local helpers ───────────────────────────────────────────────────────────

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl p-4 md:p-5" style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)" }}>
      <div className="mb-4">
        <h3 style={{ fontSize: "var(--text-base)", fontWeight: 600, color: "var(--foreground)", margin: 0 }}>{title}</h3>
        {subtitle && <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: "2px 0 0 0" }}>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--muted)", border: `1px solid ${warn ? "var(--chart-3)" : "var(--border)"}` }}>
      <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginBottom: 2, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: "var(--text-lg)", fontWeight: 700, color: warn ? "var(--chart-3)" : "var(--foreground)" }}>{value}</div>
    </div>
  );
}

function KappaStat({ label, k, strength }: { label: string; k: number | null | undefined; strength?: string }) {
  const val = k == null ? null : Number(k);
  const color =
    val == null ? "var(--muted-foreground)"
    : val >= 0.8 ? "var(--alpha-teal)"
    : val >= 0.6 ? "var(--chart-3)"
    : "var(--infrared-coral)";
  return (
    <div className="p-3 rounded-lg" style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}>
      <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginBottom: 2, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: "var(--text-lg)", fontWeight: 700, color }}>
        {val == null ? "n/a" : val.toFixed(3)}
      </div>
      {strength && <div style={{ fontSize: 10, color }}>{strength}</div>}
    </div>
  );
}

function ToggleBtn({ active, color, onClick, children }: { active: boolean; color: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md"
      style={{
        backgroundColor: active ? color : "var(--muted)",
        color: active ? "white" : "var(--foreground)",
        border: `1px solid ${active ? color : "var(--border)"}`,
        fontSize: "var(--text-xs)", fontWeight: 600, cursor: "pointer",
      }}>
      {children}
    </button>
  );
}

function RunningRow({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 py-4">
      <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--alpha-teal)" }} />
      <span style={pMuted}>{label}</span>
    </div>
  );
}

const navBtn = (disabled: boolean): React.CSSProperties => ({
  background: "transparent",
  color: disabled ? "var(--border)" : "var(--muted-foreground)",
  border: "1px solid var(--border)",
  cursor: disabled ? "not-allowed" : "pointer",
});
const inputStyle: React.CSSProperties = {
  padding: "8px 12px", fontSize: "var(--text-sm)", color: "var(--foreground)",
  backgroundColor: "var(--background)", border: "1px solid var(--border)",
  borderRadius: 8, outline: "none", fontFamily: "inherit",
};
const labelStyle: React.CSSProperties = {
  color: "var(--muted-foreground)", letterSpacing: "0.05em", fontWeight: 600,
};
const pMuted: React.CSSProperties = {
  fontSize: "var(--text-sm)", color: "var(--muted-foreground)",
};